import React from "react";
import { FaAppleAlt } from "react-icons/fa";
import useSystemVariable from "./shared/useSystemVariable";
import SystemVariablePage from "./shared/SystemVariablePage";
import { normalizeHeader, pickCell, parseNumber, safeNumber, downloadExcelTemplate } from "./shared/excelUtils";
import {
  useBulkUpsertFoodsMutation, useCreateFoodMutation,
  useListFoodsMutation, useDeleteFoodMutation, useUpdateFoodMutation,
} from "../../store/systemVariablesApi";

const DEFAULT_FORM = {
  name: "", amount: "", amountUnit: "", fat: "", carbs: "",
  protein: "", calories: "", glycemicIndex: "", nutritionValue: "", notes: "",
};

const itemToForm = (f) => ({
  name: f?.name || "",
  amount: safeNumber(f?.amount),
  amountUnit: f?.amountUnit || "",
  fat: safeNumber(f?.fat),
  carbs: safeNumber(f?.carbs),
  protein: safeNumber(f?.protein),
  calories: safeNumber(f?.calories),
  glycemicIndex: safeNumber(f?.glycemicIndex),
  nutritionValue: f?.nutritionValue || "",
  notes: f?.notes || "",
});

const formToPayload = (form) => ({
  name: form.name,
  amount: parseNumber(form.amount),
  amountUnit: form.amountUnit || undefined,
  fat: parseNumber(form.fat),
  carbs: parseNumber(form.carbs),
  protein: parseNumber(form.protein),
  calories: parseNumber(form.calories),
  glycemicIndex: parseNumber(form.glycemicIndex),
  nutritionValue: form.nutritionValue || undefined,
  notes: form.notes || undefined,
});

const mapExcelRow = (row) => {
  const n = {};
  Object.keys(row || {}).forEach((k) => { n[normalizeHeader(k)] = row[k]; });
  return {
    name: (pickCell(n, ["besin adı", "besin adi", "ad", "isim", "name"]) || "").toString().trim(),
    amount: parseNumber(pickCell(n, ["miktar", "amount"])),
    amountUnit: (pickCell(n, ["birim", "miktar birimi", "unit", "amount unit"]) || "").toString().trim(),
    fat: parseNumber(pickCell(n, ["yağ", "yag", "fat"])),
    carbs: parseNumber(pickCell(n, ["kh", "karbonhidrat", "carb", "carbs"])),
    protein: parseNumber(pickCell(n, ["protein"])),
    calories: parseNumber(pickCell(n, ["kalori", "kcal", "calorie", "calories"])),
    glycemicIndex: parseNumber(pickCell(n, ["glisemik index", "glisemik indeks", "gi", "glycemic index"])),
    nutritionValue: (pickCell(n, ["besin değ", "besin değeri", "besin degeri", "nutrition value"]) || "").toString().trim(),
    notes: (pickCell(n, ["notlar", "not", "notes"]) || "").toString().trim(),
  };
};

const TEMPLATE_ROWS = [
  { "Besin Adı": "Elma", Miktar: 100, Birim: "g", Yağ: 0.2, KH: 14, Protein: 0.3, Kalori: 52, "Glisemik Index": 38, "Besin değ.": "Örnek", Notlar: "" },
  { "Besin Adı": "Muz", Miktar: 100, Birim: "g", Yağ: 0.3, KH: 23, Protein: 1.1, Kalori: 89, "Glisemik Index": 51, "Besin değ.": "Potasyum", Notlar: "" },
  { "Besin Adı": "Yoğurt", Miktar: 200, Birim: "g", Yağ: 3.8, KH: 9.6, Protein: 7.0, Kalori: 120, "Glisemik Index": 35, "Besin değ.": "Probiyotik", Notlar: "" },
];

const TABLE_COLUMNS = [
  { key: "name", label: "Besin Adı", bold: true },
  { key: "amount", label: "Miktar", width: 110, render: (f) => `${f.amount ?? "-"} ${f.amountUnit || ""}` },
  { key: "fat", label: "Yağ", width: 90 },
  { key: "carbs", label: "KH", width: 90 },
  { key: "protein", label: "Protein", width: 90 },
  { key: "calories", label: "Kalori", width: 90 },
  { key: "glycemicIndex", label: "GI", width: 80 },
  { key: "notes", label: "Notlar", render: (f) => f.notes || "-" },
];

const FORM_SECTIONS = [
  { title: "Temel Bilgiler", fields: [
    { key: "name", label: "Besin Adı", required: true },
    { key: "nutritionValue", label: "Besin değ." },
    { key: "notes", label: "Notlar", multiline: true },
  ]},
  { title: "Miktar", fields: [
    { key: "amount", label: "Miktar", flex: "1 1 160px" },
    { key: "amountUnit", label: "Birim (g/ml/adet)", flex: "1 1 160px" },
  ]},
  { title: "Makro Değerler", fields: [
    { key: "fat", label: "Yağ", flex: "1 1 140px" },
    { key: "carbs", label: "KH", flex: "1 1 140px" },
    { key: "protein", label: "Protein", flex: "1 1 140px" },
    { key: "calories", label: "Kalori", flex: "1 1 140px" },
    { key: "glycemicIndex", label: "Glisemik İndex (GI)", flex: "1 1 160px" },
  ]},
];

const FORM_FIELDS = [
  { key: "name", label: "Besin Adı", required: true },
  { key: "amount", label: "Miktar" },
  { key: "amountUnit", label: "Birim" },
  { key: "fat", label: "Yağ" },
  { key: "carbs", label: "KH" },
  { key: "protein", label: "Protein" },
  { key: "calories", label: "Kalori" },
  { key: "glycemicIndex", label: "GI" },
  { key: "nutritionValue", label: "Besin değ." },
  { key: "notes", label: "Notlar", multiline: true },
];

const IMPORT_COLUMNS = [
  { key: "name", label: "Besin Adı", bold: true },
  { key: "amount", label: "Miktar", render: (r) => `${r.amount ?? "-"} ${r.amountUnit || ""}` },
  { key: "fat", label: "Yağ" },
  { key: "carbs", label: "KH" },
  { key: "protein", label: "Protein" },
  { key: "calories", label: "Kalori" },
  { key: "glycemicIndex", label: "GI" },
  { key: "notes", label: "Notlar" },
];

export default function Foods() {
  const vm = useSystemVariable({
    listMutation: useListFoodsMutation(),
    createMutation: useCreateFoodMutation(),
    updateMutation: useUpdateFoodMutation(),
    deleteMutation: useDeleteFoodMutation(),
    bulkMutation: useBulkUpsertFoodsMutation(),
    itemsKey: "foods",
    idKey: "foodId",
    defaultForm: DEFAULT_FORM,
    itemToForm,
    formToPayload,
    entityLabel: "Besin",
  });

  return (
    <SystemVariablePage
      icon={<FaAppleAlt size={22} />}
      title="Besin Şablonları"
      subtitle={<>Diyet oluşturma ekranında seçilecek besin listesi. Zorunlu alan: <b>Besin Adı</b></>}
      entityLabel="Besin"
      vm={vm}
      tableColumns={TABLE_COLUMNS}
      formFields={FORM_FIELDS}
      formSections={FORM_SECTIONS}
      importColumns={IMPORT_COLUMNS}
      mapExcelRow={mapExcelRow}
      downloadTemplate={() => downloadExcelTemplate(TEMPLATE_ROWS, "Besinler", "besin_sablonu.xlsx")}
    />
  );
}
